/**
 * ProjectSettings page - Manage project settings and brand context
 * Includes tabs for general settings, brand context configuration, and danger zone
 */

import { ArrowLeft, Save, Loader2, Trash2 } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import type { ChangeEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { BrandContextForm } from '@/components/project/BrandContextForm';
import { TaxonomySchemaBuilder } from '@/components/taxonomy';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ConfirmDeleteModal } from '@/components/ui/ConfirmDeleteModal';
import { Input } from '@/components/ui/Input';
import { useProject, useUpdateProject, useDeleteProject } from '@/hooks/useProjects';
import { useTaxonomySchema, useSaveTaxonomySchema } from '@/hooks/useTaxonomy';
import { useTemplates } from '@/hooks/useTemplates';
import { cn } from '@/lib/utils';
import { uploadLogo } from '@/services/upload';
import type { BrandContext, UpdateProjectRequest } from '@/types';
import type { TaxonomyField } from '@/types/taxonomy';

// ============================================================================
// TYPES
// ============================================================================

type TabType = 'general' | 'brand-context' | 'taxonomy' | 'template' | 'danger';

interface GeneralFormData {
  name: string;
  brandName: string;
  description: string;
}

interface FormErrors {
  name?: string;
  brandName?: string;
  description?: string;
}

// ============================================================================
// TAB NAVIGATION COMPONENT
// ============================================================================

interface TabButtonProps {
  label: string;
  value: TabType;
  active: boolean;
  onClick: () => void;
}

function TabButton({ label, value: _value, active, onClick }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-4 py-2 text-sm font-semibold rounded-lg transition-all',
        active
          ? 'bg-brand/10 text-brand'
          : 'text-secondary hover:bg-surface-subtle hover:text-primary'
      )}
      aria-current={active ? 'page' : undefined}
    >
      {label}
    </button>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ProjectSettings() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: project, isLoading, error } = useProject(projectId!);
  const updateProjectMutation = useUpdateProject(projectId!);
  const deleteProjectMutation = useDeleteProject(projectId!);
  const { data: taxonomySchema, isLoading: isTaxonomyLoading } = useTaxonomySchema(projectId!);
  const saveTaxonomyMutation = useSaveTaxonomySchema(projectId!);

  // Template data
  const { data: templateListData } = useTemplates(project?.teamId);
  const allTemplates = useMemo(() => templateListData?.templates ?? [], [templateListData?.templates]);
  // Determine which template is currently active
  const activeTemplateId = project?.templateId || '';
  const selectedTemplate = useMemo(
    () => allTemplates.find((t) => t.templateId === activeTemplateId) ?? null,
    [allTemplates, activeTemplateId]
  );

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTemplateChangeConfirm, setShowTemplateChangeConfirm] = useState(false);
  const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(null);

  // Tab state (from URL or default to general)
  const [activeTab, setActiveTab] = useState<TabType>(
    (searchParams.get('tab') as TabType) || 'general'
  );

  // General settings form state
  const [generalFormData, setGeneralFormData] = useState<GeneralFormData>({
    name: '',
    brandName: '',
    description: '',
  });
  const [generalFormErrors, setGeneralFormErrors] = useState<FormErrors>({});
  const [hasGeneralChanges, setHasGeneralChanges] = useState(false);

  // Brand context form state
  const [brandContext, setBrandContext] = useState<BrandContext>({});
  const [hasBrandContextChanges, setHasBrandContextChanges] = useState(false);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Sync form data with project data
  useEffect(() => {
    if (project) {
      setGeneralFormData({
        name: project.name,
        brandName: project.brandName || '',
        description: project.description || '',
      });
      setBrandContext(project.brandContext || {});
    }
  }, [project]);

  // Sync tab with URL
  useEffect(() => {
    const tabParam = searchParams.get('tab') as TabType;
    if (tabParam && (tabParam === 'general' || tabParam === 'brand-context' || tabParam === 'taxonomy' || tabParam === 'template' || tabParam === 'danger')) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // ============================================================================
  // VALIDATION
  // ============================================================================

  const validateGeneralForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!generalFormData.name.trim()) {
      newErrors.name = 'Project name is required';
    } else if (generalFormData.name.trim().length < 2) {
      newErrors.name = 'Project name must be at least 2 characters';
    } else if (generalFormData.name.trim().length > 100) {
      newErrors.name = 'Project name must be less than 100 characters';
    }

    if (generalFormData.brandName.trim() && generalFormData.brandName.trim().length > 100) {
      newErrors.brandName = 'Brand name must be less than 100 characters';
    }

    if (generalFormData.description.trim() && generalFormData.description.trim().length > 500) {
      newErrors.description = 'Description must be less than 500 characters';
    }

    setGeneralFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const handleGeneralInputChange = (field: keyof GeneralFormData) => (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setGeneralFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
    setHasGeneralChanges(true);
    if (generalFormErrors[field]) {
      setGeneralFormErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleSaveGeneral = async () => {
    if (!validateGeneralForm()) {
      return;
    }

    const updateData: UpdateProjectRequest = {
      name: generalFormData.name.trim(),
      brandName: generalFormData.brandName.trim() || undefined,
      description: generalFormData.description.trim() || undefined,
    };

    try {
      await updateProjectMutation.mutateAsync(updateData);
      setHasGeneralChanges(false);
    } catch (error) {
      console.error('Failed to update project:', error);
      alert('Failed to update project. Please try again.');
    }
  };

  const handleBrandContextChange = (newBrandContext: BrandContext) => {
    setBrandContext(newBrandContext);
    setHasBrandContextChanges(true);
  };

  const handleLogoUpload = async (file: File): Promise<string> => {
    if (!projectId) {
      throw new Error('Project ID is required');
    }

    const s3Key = await uploadLogo(projectId, file);

    // Return the S3 key - backend will generate presigned download URL
    return s3Key;
  };

  const handleSaveBrandContext = async () => {
    const updateData: UpdateProjectRequest = {
      brandContext,
    };

    try {
      await updateProjectMutation.mutateAsync(updateData);
      setHasBrandContextChanges(false);
    } catch (error) {
      console.error('Failed to update brand context:', error);
      alert('Failed to update brand context. Please try again.');
    }
  };

  const handleBackToProject = () => {
    navigate(`/projects/${projectId}`);
  };

  const handleDeleteProject = async () => {
    await deleteProjectMutation.mutateAsync();
    setShowDeleteModal(false);
    navigate('/projects');
  };

  const handleSaveTaxonomySchema = async (fields: Omit<TaxonomyField, 'createdAt'>[]) => {
    try {
      await saveTaxonomyMutation.mutateAsync(fields);
    } catch (error) {
      console.error('Failed to save taxonomy schema:', error);
      alert('Failed to save taxonomy schema. Please try again.');
    }
  };

  const handleTemplateChange = (templateId: string | null) => {
    setPendingTemplateId(templateId);
    setShowTemplateChangeConfirm(true);
  };

  const handleTemplateChangeConfirm = async () => {
    setShowTemplateChangeConfirm(false);
    try {
      await updateProjectMutation.mutateAsync({
        templateId: pendingTemplateId,
      });
    } catch (error) {
      console.error('Failed to update template:', error);
      alert('Failed to update template. Please try again.');
    }
  };


  // ============================================================================
  // LOADING & ERROR STATES
  // ============================================================================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 text-brand animate-spin" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <div className="text-center py-12">
            <h3 className="text-lg font-semibold text-danger mb-2">
              Failed to load project
            </h3>
            <p className="text-sm text-secondary mb-6">
              {error?.message || 'Project not found'}
            </p>
            <Button onClick={() => navigate('/projects')} variant="secondary">
              Back to Projects
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Page Header */}
      <div>
        <button
          onClick={handleBackToProject}
          className="inline-flex items-center gap-2 text-sm font-medium text-secondary hover:text-primary transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Project
        </button>

        <div className="flex items-start justify-between gap-6">
          <div>
            <h1 className="text-3xl font-black text-primary leading-tight tracking-tight">
              {project.name}
            </h1>
            <p className="text-sm text-secondary mt-2">Project Settings</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-border-subtle pb-1">
        <TabButton
          label="General"
          value="general"
          active={activeTab === 'general'}
          onClick={() => handleTabChange('general')}
        />
        <TabButton
          label="Brand Context"
          value="brand-context"
          active={activeTab === 'brand-context'}
          onClick={() => handleTabChange('brand-context')}
        />
        <TabButton
          label="Taxonomy"
          value="taxonomy"
          active={activeTab === 'taxonomy'}
          onClick={() => handleTabChange('taxonomy')}
        />
        <TabButton
          label="Template"
          value="template"
          active={activeTab === 'template'}
          onClick={() => handleTabChange('template')}
        />
        <TabButton
          label="Danger Zone"
          value="danger"
          active={activeTab === 'danger'}
          onClick={() => handleTabChange('danger')}
        />
      </div>

      {/* Tab Content */}
      {activeTab === 'general' && (
        <Card>
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-primary mb-1">
                General Settings
              </h2>
              <p className="text-sm text-secondary">
                Update your project&apos;s basic information
              </p>
            </div>

            <div className="space-y-6">
              <Input
                label="Project Name"
                value={generalFormData.name}
                onChange={handleGeneralInputChange('name')}
                placeholder="e.g., Summer Campaign 2024"
                error={!!generalFormErrors.name}
                errorMessage={generalFormErrors.name}
                required
                disabled={updateProjectMutation.isPending}
              />

              <Input
                label="Brand Name"
                value={generalFormData.brandName}
                onChange={handleGeneralInputChange('brandName')}
                placeholder="e.g., Nike, Coca-Cola"
                error={!!generalFormErrors.brandName}
                errorMessage={generalFormErrors.brandName}
                disabled={updateProjectMutation.isPending}
              />

              <div>
                <label
                  htmlFor="description"
                  className="mb-2 block text-sm font-medium text-secondary"
                >
                  Description
                </label>
                <textarea
                  id="description"
                  value={generalFormData.description}
                  onChange={handleGeneralInputChange('description')}
                  placeholder="Add any additional details about this project..."
                  rows={4}
                  className="flex w-full font-sans text-md text-primary placeholder:text-muted transition-all duration-base ease-standard focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 rounded-md border border-border-subtle bg-surface px-4 py-3 focus-visible:border-brand/55 focus-visible:ring-4 focus-visible:ring-brand/12 resize-none"
                  disabled={updateProjectMutation.isPending}
                  aria-invalid={!!generalFormErrors.description}
                />
                {generalFormErrors.description && (
                  <p className="mt-2 text-xs text-danger" role="alert">
                    {generalFormErrors.description}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-border-subtle">
              <Button
                onClick={handleSaveGeneral}
                leftIcon={<Save className="h-4 w-4" />}
                loading={updateProjectMutation.isPending}
                disabled={!hasGeneralChanges || updateProjectMutation.isPending}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'brand-context' && (
        <Card>
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-primary mb-1">
                Brand Context
              </h2>
              <p className="text-sm text-secondary">
                Configure your brand identity to improve AI analysis accuracy
              </p>
            </div>

            <BrandContextForm
              brandContext={brandContext}
              onChange={handleBrandContextChange}
              onUploadLogo={handleLogoUpload}
              isLoading={updateProjectMutation.isPending}
            />

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-border-subtle">
              <Button
                onClick={handleSaveBrandContext}
                leftIcon={<Save className="h-4 w-4" />}
                loading={updateProjectMutation.isPending}
                disabled={!hasBrandContextChanges || updateProjectMutation.isPending}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'taxonomy' && (
        <Card>
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-primary mb-1">
                Creative Taxonomy
              </h2>
              <p className="text-sm text-secondary">
                Define classification fields for automatic creative tagging during AI analysis
              </p>
            </div>

            <TaxonomySchemaBuilder
              schema={taxonomySchema ?? null}
              onSave={handleSaveTaxonomySchema}
              isLoading={isTaxonomyLoading}
              isSaving={saveTaxonomyMutation.isPending}
            />
          </div>
        </Card>
      )}

      {activeTab === 'template' && (
        <Card>
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-primary mb-1">
                Analysis Template
              </h2>
              <p className="text-sm text-secondary">
                Choose the measurement philosophy for this project&apos;s creative analyses.
              </p>
            </div>

            {/* Unified Template selector */}
            <div className="max-w-md">
              <label className="block text-sm font-medium text-secondary mb-1">
                Analysis Template
              </label>
              <select
                value={activeTemplateId}
                onChange={(e) => handleTemplateChange(e.target.value || null)}
                className="w-full rounded-lg border border-border-subtle bg-surface px-3 py-2 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand/55"
              >
                <option value="">System Default</option>
                {allTemplates.map((t) => (
                  <option key={t.templateId} value={t.templateId}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Current template info */}
            {selectedTemplate && (
              <div className="p-4 bg-surface-subtle rounded-lg">
                <div className="text-sm font-medium text-primary">{selectedTemplate.name}</div>
                {selectedTemplate.description && (
                  <div className="text-sm text-secondary mt-1">{selectedTemplate.description}</div>
                )}
                <div className="text-xs text-muted mt-2">
                  {selectedTemplate.gates.length} quality gates
                  {' \u2022 '}
                  {selectedTemplate.gates.filter((g) => g.role === 'critical').length} critical,{' '}
                  {selectedTemplate.gates.filter((g) => g.role === 'standard').length} standard
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {activeTab === 'danger' && (
        <Card className="border-danger/30">
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-danger mb-1">
                Danger Zone
              </h2>
              <p className="text-sm text-secondary">
                Irreversible and destructive actions
              </p>
            </div>

            <div className="p-4 rounded-lg border border-danger/30 bg-danger-soft/30">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-primary mb-1">
                    Delete this project
                  </h3>
                  <p className="text-sm text-secondary">
                    Once you delete a project, there is no going back. This will soft-delete the project and all its creatives.
                    The data will be retained for 30 days before permanent deletion.
                  </p>
                </div>
                <Button
                  variant="danger"
                  onClick={() => setShowDeleteModal(true)}
                  leftIcon={<Trash2 className="h-4 w-4" />}
                >
                  Delete Project
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteProject}
        title="Delete Project"
        description="This project will be soft-deleted and retained for 30 days before permanent deletion. All creatives and analyses will be inaccessible during this period."
        itemName={project.name}
        confirmText="Delete Project"
        isLoading={deleteProjectMutation.isPending}
        variant="danger"
      />

      <ConfirmDeleteModal
        isOpen={showTemplateChangeConfirm}
        onClose={() => setShowTemplateChangeConfirm(false)}
        onConfirm={handleTemplateChangeConfirm}
        title="Change Template"
        description="Changing the template will affect future analyses only. Existing analyses are unchanged. Re-analyze creatives to apply the new template."
        confirmText="Change Template"
        variant="warning"
      />
    </div>
  );
}
